import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { X } from "lucide-react";
import Button from "../../components/ui/Button";
import { getBase64Image } from "../../utils/imageHelper";
import { loadStripe } from "@stripe/stripe-js";
import { CheckCircle, Clock, Calendar, Users } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Lottie from "lottie-react";
import Loading from "../../assets/Loading.json";
import { useAuthStore } from "../../store/authStore";
import { host } from "../../api/ApiCalls";
import { toast } from "react-toastify";
import { tostifyOption } from "../../utils/toastOptions";

const stripePromise = loadStripe(
  "pk_test_51ONdWpLtBDVl35jcXuH0VBAo16nJGCF5VffW1utKb9J1dCJXCC2GzxChU3SZscbYIchlEJyjxK814BtUGaNI9MHc00EpNDPd4T"
);

const CourseDetailComponent = () => {
  const { id } = useParams();
  const { user } = useAuthStore(); // Assuming you have a store for auth
  const navigate = useNavigate();
  const location = useLocation();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [isStudent, setIsStudent] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [sessionSlots, setSessionSlots] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [enrollLoading, setEnrollLoading] = useState(false);

  const isAuthenticated = Boolean(localStorage.getItem("academiaHubToken")); // 🔐 Or use your auth logic

  console.log("isAuthenticated: ", isAuthenticated);
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const response = await axios.get(`${host}/api/courses/${id}`, {
          withCredentials: true,
        });

        setCourse(response.data.course);
        console.log("Courses: ", response.data.course);
        setTeacher(response.data.course.teacher);
        setIsEnrolled(response.data.isEnrolled);
        setIsTeacher(response.data.isTeacher);
        setIsStudent(!response.data.isTeacher);
      } catch (error) {
        console.error("Error fetching course:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id]);

  // Function to get upcoming dates for a selected day
  const getUpcomingDates = (day) => {
    let dates = [];
    let today = new Date();
    for (let i = 0; i < 185; i++) {
      let futureDate = new Date();
      futureDate.setDate(today.getDate() + i);
      if (futureDate.toLocaleDateString("en-US", { weekday: "long" }) === day) {
        dates.push(futureDate);
      }
    }
    return dates;
  };

  // When a day is selected, fetch available dates and prepare slots
  const handleDaySelect = (day, slots) => {
    const dates = getUpcomingDates(day);
    setAvailableDates(dates);
    setSelectedDay(day);
    setSessionSlots(slots);
    setSelectedDate(null);
  };

  // Handling slot selection
  const handleSlotSelection = (slot) => {
    if (!selectedDate) {
      alert("Please select a date first.");
      return;
    }
    if (selectedSlots.length >= course.totalSessions) {
      alert(`You can only select ${course.totalSessions} sessions.`);
      return;
    }

    const newSlot = { date: selectedDate, day: selectedDay, ...slot };
    setSelectedSlots([...selectedSlots, newSlot]);
  };

  // Handling slot removal
  const handleRemoveSlot = (index) => {
    const updatedSlots = selectedSlots.filter((slot, i) => i !== index);
    setSelectedSlots(updatedSlots);
  };
  console.log("Selected Slots: ", selectedSlots);
  // Handling enrollment
  const handleEnroll = async () => {
    setEnrollLoading(true);
    if (selectedSlots.length !== course.totalSessions) {
      toast(`Please select ${course.totalSessions} sessions.`, tostifyOption);
      setEnrollLoading(false);
      return;
    }

    try {
      const stripe = await stripePromise;
      const response = await axios.post(
        `${host}/api/payments/create-checkout-session`,
        {
          courseId: course._id,
          courseName: course.title,
          price: course.price,
          selectedSlots,
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        console.error("❌ No URL received from backend!");
      }
      setEnrollLoading(false);
    } catch (error) {
      console.error(
        "❌ Payment error:",
        error.response ? error.response.data : error
      );
    }
    setEnrollLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center text-gray-800 m-8">
        <div className="w-80">
          <Lottie animationData={Loading} loop={true} />
        </div>
      </div>
    );
  }
  if (!course)
    return (
      <div className="text-center mt-10 text-red-500">Course not found.</div>
    );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h1 className="text-4xl font-bold mb-6">{course.title}</h1>
              <p className="text-light-600 mb-4">{course.description}</p>

              {isEnrolled ? (
                <div className="flex items-center space-x-4 mt-6">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="text-green-500">
                    You are enrolled in this course
                  </span>
                </div>
              ) : (
                !isTeacher && (
                  <div className="flex items-center space-x-4 mt-6">
                    <Button
                      onClick={() => {
                        if (isAuthenticated) {
                          setShowModal(true); // ✅ Show enrollment modal
                        } else {
                          navigate("/login", {
                            state: { from: location.pathname }, // Save current path
                          });
                        }
                      }}
                    >
                      Enroll Now for ${course.price}
                    </Button>
                  </div>
                )
              )}

              {/* {isTeacher && (
                <div className="flex items-center space-x-4 mt-6">
                  <Button onClick={() => navigate(`/courses/${id}/portal`)}>
                    Go To Course
                  </Button>
                </div>
              )} */}

              <div
                className="flex items-center space-x-4 mt-6 cursor-pointer"
                onClick={() => navigate(`/teachers/${teacher._id}`)}
              >
                <div className="flex items-center space-x-2 bg-white text-gray-800 p-2 rounded-lg shadow-sm w-64 max-w-xs">
                  {/* ✅ Rounded Image */}
                  <img
                    src={teacher.avatar}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover"
                  />

                  {/* ✅ Teacher Name */}
                  <h3 className="font-semibold">{teacher.name}</h3>
                </div>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-2xl">
              <img
                src={course.image}
                alt={course.title}
                className="w-full h-[300px] object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Availability Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {/* Course Description */}
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold mb-4">Course Description</h2>
              <p>{course.description}</p>
            </div>
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center">
                <Calendar className="w-6 h-6 mr-2" /> Availability
              </h2>
              <div className="space-y-4">
                {course.availability.map((daySlot, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <h3 className="font-semibold text-lg">{daySlot.day}</h3>
                    <div className="flex space-x-2 mt-2">
                      {daySlot.slots.map((slot, slotIndex) => (
                        <span
                          key={slotIndex}
                          className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-md text-sm"
                        >
                          {slot.startTime} - {slot.endTime}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Course Info Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-8 shadow-sm sticky top-8">
              <h3 className="text-2xl font-bold mb-6">Key Features:</h3>
              <div className="flex items-center space-x-4 mb-6">
                <Users className="h-5 w-5" />
                <span>{course.students?.length || 0} students enrolled</span>
              </div>
              <div className="flex items-center space-x-4 mb-6">
                <Clock className="h-5 w-5" />
                <span>{course.duration} hours</span>
              </div>
              <div className="flex items-center space-x-4">
                <Calendar className="h-5 w-5" />
                <span>{course.totalSessions} sessions</span>
              </div>

              {isEnrolled ? (
                <Button
                  className="w-full mt-8"
                  onClick={() =>
                    user.role === "student"
                      ? navigate(`/courses/${id}/${user.id}`)
                      : null
                  }
                >
                  Go To Course
                </Button>
              ) : isStudent ? (
                <Button
                  onClick={() => {
                    if (isAuthenticated) {
                      setShowModal(true); // ✅ Show enrollment modal
                    } else {
                      navigate("/login", {
                        state: { from: location.pathname }, // Save current path
                      });
                    }
                  }}
                  className="w-full mt-8"
                >
                  Enroll Now for ${course.price}
                </Button>
              ) : (
                null
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Slot Selection Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center p-4">
          <div
            className="bg-white p-6 shadow-2xl w-full max-w-2xl border border-gray-200"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-2xl font-semibold">Select Time Slots ({course.totalSessions} session/s)</h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-5 h-5 text-gray-600 hover:text-gray-800" />
              </button>
            </div>

            {/* Step 1: Select Day */}
            <h3 className="font-semibold mb-2">Select Available Day:</h3>
            <div className="flex flex-wrap gap-2">
              {course.availability.map((daySlot, index) => (
                <button
                  key={index}
                  onClick={() => handleDaySelect(daySlot.day, daySlot.slots)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    selectedDay === daySlot.day
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300"
                  }`}
                >
                  {daySlot.day}
                </button>
              ))}
            </div>

            {/* Step 2: Select Date */}
            {availableDates.length > 0 && (
              <>
                <h3 className="font-semibold mt-4">Select a Date:</h3>
                <DatePicker
                  selected={selectedDate}
                  onChange={(date) => setSelectedDate(date)}
                  includeDates={availableDates}
                  dateFormat="dd MMM yyyy"
                  className="border p-2 rounded w-full mt-2"
                />
              </>
            )}

            {/* Step 3: Select Slot */}
            {selectedDate && (
              <>
                <h3 className="font-semibold mt-4">Select a Time Slot:</h3>
                <div className="flex flex-wrap gap-2 mt-2">
                  {sessionSlots.map((slot, slotIndex) => {
                    const isBooked = course.bookedSlots?.some(
                      (booked) =>
                        booked.day === selectedDay &&
                        new Date(booked.date).toDateString() ===
                          selectedDate.toDateString() &&
                        booked.startTime === slot.startTime &&
                        booked.endTime === slot.endTime
                    );

                    return (
                      <button
                        key={slotIndex}
                        onClick={() => handleSlotSelection(slot)}
                        disabled={isBooked}
                        className={`px-3 py-1 rounded-md text-sm 
                    ${
                      selectedSlots.some(
                        (s) =>
                          s.startTime === slot.startTime &&
                          s.date.toDateString() === selectedDate.toDateString()
                      )
                        ? "bg-indigo-600 text-white"
                        : ""
                    }
                    ${
                      isBooked
                        ? "bg-red-300 text-white cursor-not-allowed"
                        : "bg-gray-200 hover:bg-gray-300"
                    }
                  `}
                      >
                        {slot.startTime} - {slot.endTime}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Selected Slots Summary */}
            {selectedSlots.length > 0 && (
              <div className="mt-4 p-3 border rounded-md bg-gray-100">
                <h3 className="font-semibold">Selected Sessions:</h3>
                <ul className="mt-2 space-y-2">
                  {selectedSlots.map((slot, index) => (
                    <li
                      key={index}
                      className="flex justify-between items-center bg-white p-2 rounded shadow"
                    >
                      <div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-5 w-5" />{" "}
                          {slot.date.toLocaleDateString("en-US")} ({slot.day})
                        </div>
                        <div className="flex items-center gap-1">
                          {" "}
                          <Clock className="h-5 w-5" /> {slot.startTime} -{" "}
                          {slot.endTime}{" "}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveSlot(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Button onClick={handleEnroll} className="w-full mt-4" disabled={enrollLoading}>
              {enrollLoading ? (
    <div className="flex items-center justify-center gap-2">
      <svg
        className="animate-spin h-5 w-5 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
        />
      </svg>
      Redirecting...
    </div>
  ) : (
    "Proceed to Payment"
  )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetailComponent;
